/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable import/order */
import {observable, runInAction} from 'mobx';

import {FooterData} from '@udemy/footer';
import {getClientAppContext} from '@udemy/nextjs-core';
import {HeaderData} from '@udemy/react-header';
import {CommonStore} from '@udemy/store-provider';

// <<Stencil::Block(userImports)>>
import {bootstrapBraze} from '@udemy/braze';
import {initializeOneTrust} from '@udemy/onetrust';
import {UDData} from '@udemy/ud-data';
import {initializeEventTracking} from 'src/lib/event-tracking';
import {initializePerformanceTracking} from 'src/lib/performance';
import {initializeSentryClient} from 'src/lib/sentry';
import {loadGTag} from '../lib/gtm';
import {provideMarketingAttribution} from '../lib/marketing-attribution';
import {AppContextData} from '../pages/api/context.api';
import {DesktopInstructorHeaderProps} from '@udemy/react-header';
// <</Stencil::Block>>

export interface RootStoreData {
    footerData: FooterData;
    headerData: HeaderData;
    locale: string;
    // <<Stencil::Block(rootStoreData)>>
    instructorHeaderData?: Pick<DesktopInstructorHeaderProps, 'instructorContext'>;
    tipaltiPaymentsHistoryLink: string | undefined;
    // <</Stencil::Block>>
}

// <<Stencil::Block(userDefined)>>

// <</Stencil::Block>>

/**
 * RootStore
 * This is a store class that acts a convienent place to store global application data that can be accessed via `useStores([RootStore])`.
 */
export class RootStore extends CommonStore {
    static readonly STORE_ID = 'RootStore';

    @observable headerData: HeaderData;
    @observable footerData: FooterData;

    // <<Stencil::Block(classProperties)>>
    @observable isClientDataLoaded = false;
    @observable tipaltiPaymentsHistoryLink: string | undefined;
    @observable instructorHeaderData?: Pick<DesktopInstructorHeaderProps, 'instructorContext'>;
    // <</Stencil::Block>>

    constructor(private readonly data: RootStoreData) {
        super();

        // Store initialization data
        this.headerData = data.headerData;
        this.footerData = data.footerData;

        // <<Stencil::Block(userConstructor)>>
        this.tipaltiPaymentsHistoryLink = data.tipaltiPaymentsHistoryLink;
        this.instructorHeaderData = data.instructorHeaderData;
        // <</Stencil::Block>>
    }

    // <<Stencil::Block(userMethods)>>
    async initializeClient(udData: UDData) {
        await this.handleUserContextReady(udData);
    }
    handleUserContextReady = async (udData: UDData) => {
        initializeOneTrust();
        initializePerformanceTracking(udData);
        await this.loadClientData(udData);
        // Right now there are some dependencies on client data so we need to wait for it to load before initializing
        initializeEventTracking(udData, this.headerData, this.data.locale);
        initializeSentryClient(udData, this.headerData);
        loadGTag(udData, this.data.locale);
        provideMarketingAttribution();
        bootstrapBraze();
    };

    /**
     * Fetch client-side UD Global data and update stores that depend on it
     */
    private async loadClientData(udData: UDData) {
        const clientAppContext = await getClientAppContext<AppContextData>();

        runInAction(() => {
            const {header, footer, instructorHeaderData, tipaltiPaymentHistoryLink} =
                clientAppContext;

            this.headerData = {
                ...this.headerData,
                ...header,
                isLoggedIn: udData.me.is_authenticated,
                isClientDataLoaded: true,
            };
            this.footerData = {
                ...this.footerData,
                ...footer,
            };
            this.isClientDataLoaded = true;
            this.tipaltiPaymentsHistoryLink = tipaltiPaymentHistoryLink;
            this.instructorHeaderData = instructorHeaderData;
        });
    }
    // <</Stencil::Block>>
}
